import { useViewport } from 'hooks/3catinfo/useViewport';
import Slider from 'components/3catinfo/Slider';
import Mosaic from 'components/3catinfo/Mosaic';
import Icona from 'components/3catinfo/ui/Icona';
import clsx from 'clsx';
import __isEmpty from 'lodash/isEmpty';
import __capitalize from 'lodash/capitalize';
import styles from './taulaExtrems.module.scss';
import Titol from 'components/3catinfo/Titol';

const llistaTipus = {
    tempMax: 'tempMax',
    tempMin: 'tempMin',
    vent: 'vent',
    pluja: 'pluja'
};

const formatarMilers = (str) => {
    if (typeof str !== 'string') return str;
    const match = str.match(/^(\d+)([a-zA-Z]*)$/);
    if (!match) return str;
    const numero = match[1];
    const letra = match[2];
    const numeroFormateado = new Intl.NumberFormat('ca-ES').format(Number(numero));
    return numeroFormateado + letra;
};

const getNomAndAltitud = (field) => {
    const regex = /^(.+?)(?:\s(\d+(?:\.\d+)?m))?$/;
    const matches = field?.match(regex);
    const fields = { nom: '', altitud: '' };
    if (matches) {
        fields.nom = matches[1] ? matches[1].trim() : null;
        fields.altitud = formatarMilers(matches[2] ? matches[2].trim() : null);
    }
    return fields;
};

const Item = (props) => {
    const { item, tipus } = props;
    const { nom_ok, comarca } = item;
    const font = __capitalize(item.font ?? '');
    const { nom, altitud } = getNomAndAltitud(nom_ok);
    const { isMobile } = useViewport();

    const _getValor = () => {
        switch (tipus) {
            case llistaTipus.tempMax:
                return item.temp_max;
            case llistaTipus.tempMin:
                return item.temp_min;
            case llistaTipus.vent:
                return item.vent_max;
            case llistaTipus.pluja:
                return item.pluja;
        }
    };

    const capitalizeNom = (nom) => {
        if (typeof nom !== 'string') return '';

        return nom
            .replace(/^\s*([a-záéíóúüñ])/i, (match, p1) => match.replace(p1, p1.toUpperCase()))
            .replace(/-\s*([a-záéíóúüñ])/gi, (match, p1) => match.replace(p1, p1.toUpperCase()));
    };

    const stylesValor = clsx({
        [styles.valor]: true,
        [styles.tempMax]: tipus === 'tempMax',
        [styles.tempMin]: tipus === 'tempMin'
    });

    return (
        <li className={styles.item}>
            <div className={styles.contentInfo}>
                <span className={styles.lloc}>{capitalizeNom(nom)}</span>
                <div className={styles.contentLloc}>
                    <div className={styles.infoLloc}>
                        <Icona className={styles.icona} path='el-temps' icona='map' mida='20' alt='Mapa' />
                        <span className={styles.text}>{comarca}</span>
                    </div>
                    {altitud && (
                        <div className={styles.infoLloc}>
                            <Icona className={styles.icona} path='el-temps' icona='muntanya' mida='20' alt='Muntanya' />
                            <span className={styles.text}>{altitud}</span>
                        </div>
                    )}
                </div>
                {!isMobile && !__isEmpty(font) && <span className={styles.font}>Font: {font}</span>}
            </div>
            <div className={styles.colEsq}>
                <div className={stylesValor}>{_getValor().replace('.', ',')}</div>
                {isMobile && !__isEmpty(font) && <span className={styles.font}>Font: {font}</span>}
            </div>
        </li>
    );
};

const Taula = (props) => {
    const { data, titol, tipus, limit } = props;
    const _getMesura = () => {
        switch (tipus) {
            case llistaTipus.tempMax:
                return 'ºC';
            case llistaTipus.tempMin:
                return 'ºC';
            case llistaTipus.vent:
                return 'km/h';
            case llistaTipus.pluja:
                return 'mm';
        }
    };

    return !__isEmpty(data) ? (
        <div className={styles.taula} data-testid={`taula-extrems`}>
            <div className={styles.capcaleraTaula}>
                <h3 className={styles.titol}>{titol}</h3>
                <span className={styles.mesura}>{_getMesura()}</span>
            </div>
            <ul className={styles.contentItems}>
                {data.slice(0, limit).map((item, index) => (
                    <Item key={`item-taula-extrems-${tipus}-${index}`} item={item} tipus={tipus} />
                ))}
            </ul>
        </div>
    ) : (
        <></>
    );
};

const TaulaExtrems = (props) => {
    const { tempMax, tempMin, vent, pluja } = props;
    const { isOnlyMobile } = useViewport();
    const limit = 5;

    return (
        <div className={styles.contentTaulaExtrems}>
            <div className={styles.capcalera}>
                <div className={styles.contentTitol}>
                    <Titol tag='h2' titol="Taula d'extrems" />
                </div>
            </div>
            <div className={styles.contentTaula}>
                {isOnlyMobile ? (
                    <Slider format='taulaExtrems' className={styles.slider} estilModul='taulaExtrems'>
                        {!__isEmpty(tempMax) && (
                            <Taula
                                data={tempMax}
                                titol='Temperatures màximes'
                                tipus={llistaTipus.tempMax}
                                key='taula-extrems-tmp-max'
                                limit={limit}
                            />
                        )}
                        {!__isEmpty(tempMin) && (
                            <Taula
                                data={tempMin}
                                titol='Temperatures mínimes'
                                tipus={llistaTipus.tempMin}
                                key='taula-extrems-tmp-min'
                                limit={limit}
                            />
                        )}
                        {!__isEmpty(vent) && (
                            <Taula
                                data={vent}
                                titol='Cops de vent'
                                tipus={llistaTipus.vent}
                                key='taula-extrems-vent'
                                limit={limit}
                            />
                        )}
                        {!__isEmpty(pluja) && (
                            <Taula data={pluja} titol='Pluja' tipus={llistaTipus.pluja} key='taula-extrems-pluja' limit={limit} />
                        )}
                    </Slider>
                ) : (
                    <Mosaic numColumns={2}>
                        {!__isEmpty(tempMax) && (
                            <Taula data={tempMax} titol='Temperatures màximes' tipus={llistaTipus.tempMax} limit={limit} />
                        )}
                        {!__isEmpty(tempMin) && (
                            <Taula data={tempMin} titol='Temperatures mínimes' tipus={llistaTipus.tempMin} limit={limit} />
                        )}
                        {!__isEmpty(vent) && <Taula data={vent} titol='Cops de vent' tipus={llistaTipus.vent} limit={limit} />}
                        {!__isEmpty(pluja) && <Taula data={pluja} titol='Pluja' tipus={llistaTipus.pluja} limit={limit} />}
                    </Mosaic>
                )}
            </div>
        </div>
    );
};

export default TaulaExtrems;
